import React, { useEffect } from 'react';

interface QualityAnalysisSectionProps {
  formData: {
    nbSacsDeclares: number;
    poidsDeclares?: number; // Poids déclaré (nécessaire pour le calcul automatique)
    nbSacsSains: number;
    nbSacsRecond: number;
    nbSacsHumides: number;
    nbSacsDechires: number;
    nbSacsRejetes: number;
    nbSacsAcceptes: number;
    poidsAcceptes: number;
    observations: string;
  };
  onInputChange: (field: string, value: any) => void;
  conditionnementCapacite?: number; // Capacité du conditionnement en kg
  typePesee?: string; // Type de pesée pour désactiver le calcul automatique pour expéditions/transferts
}

const QualityAnalysisSection: React.FC<QualityAnalysisSectionProps> = ({ 
  formData, 
  onInputChange, 
  conditionnementCapacite = 1, // Valeur par défaut de 1 kg
  typePesee = 'reception' // Type de pesée par défaut
}) => {
  const FACTEUR_RECONDITIONNEMENT = 4;
  const isExpeditionOrTransfert = typePesee === 'expedition' || typePesee === 'transfert';

  // Pour expéditions/transferts : synchroniser poidsDeclares avec nbSacsDeclares (sacs de 1kg)
  useEffect(() => {
    if (isExpeditionOrTransfert && formData.nbSacsDeclares > 0) {
      // Si le poids déclaré est différent du nombre de sacs déclarés, le mettre à jour
      const poidsDeclaresActuel = formData.poidsDeclares || 0;
      if (poidsDeclaresActuel !== formData.nbSacsDeclares) {
        onInputChange('poidsDeclares', formData.nbSacsDeclares);
      }
    }
  }, [formData.nbSacsDeclares, formData.poidsDeclares, isExpeditionOrTransfert, onInputChange]);

  // Calcul automatique du nombre de sacs acceptés
  useEffect(() => {
    if (isExpeditionOrTransfert) {
      // Pour expéditions/transferts : nbSacsAcceptes = nbSacsSains (ou nbSacsDeclares si nbSacsSains = 0)
      // Si l'utilisateur modifie nbSacsSains, on met à jour nbSacsAcceptes
      // Ne mettre à jour que si la valeur calculée est différente ET que nbSacsAcceptes est actuellement 0 ou vide
      const nbSacsAcceptesCalcules = formData.nbSacsSains > 0 ? formData.nbSacsSains : (formData.nbSacsDeclares || 0);
      // Mettre à jour seulement si la valeur calculée est différente ET que nbSacsAcceptes est vide ou égal à 0
      // Cela permet à l'utilisateur de modifier manuellement nbSacsAcceptes si nécessaire
      if (nbSacsAcceptesCalcules > 0 && (formData.nbSacsAcceptes === 0 || formData.nbSacsAcceptes === undefined || formData.nbSacsAcceptes === null)) {
        onInputChange('nbSacsAcceptes', nbSacsAcceptesCalcules);
      } else if (formData.nbSacsAcceptes !== nbSacsAcceptesCalcules && nbSacsAcceptesCalcules > 0) {
        // Si la valeur calculée change et est > 0, mettre à jour
        onInputChange('nbSacsAcceptes', nbSacsAcceptesCalcules);
      }
    } else {
      // Pour réceptions : calcul normal (sains + recond)
      const nbSacsAcceptesCalcules = (formData.nbSacsSains || 0) + (formData.nbSacsRecond || 0);
      if (formData.nbSacsAcceptes !== nbSacsAcceptesCalcules) {
        onInputChange('nbSacsAcceptes', nbSacsAcceptesCalcules);
      }
    }
  }, [formData.nbSacsSains, formData.nbSacsRecond, formData.nbSacsDeclares, formData.nbSacsAcceptes, isExpeditionOrTransfert, onInputChange]);

  // Calcul automatique du poids accepté
  useEffect(() => {
    if (isExpeditionOrTransfert) {
      // Pour expéditions/transferts : poidsAcceptes = nbSacsSains (ou nbSacsAcceptes ou nbSacsDeclares/poidsDeclares si nbSacsSains = 0)
      // Car les sacs sont de 1kg, donc le nombre de sacs = poids en kg
      // Priorité : nbSacsSains > nbSacsAcceptes > poidsDeclares > nbSacsDeclares
      const poidsAcceptesCalcules = formData.nbSacsSains > 0 
        ? formData.nbSacsSains 
        : (formData.nbSacsAcceptes > 0
            ? formData.nbSacsAcceptes
            : (formData.poidsDeclares || formData.nbSacsDeclares || 0));
      
      // Mettre à jour seulement si la valeur calculée est différente ET que poidsAcceptes est vide ou égal à 0
      // Cela permet à l'utilisateur de modifier manuellement poidsAcceptes si nécessaire
      if (poidsAcceptesCalcules > 0 && (formData.poidsAcceptes === 0 || formData.poidsAcceptes === undefined || formData.poidsAcceptes === null)) {
        onInputChange('poidsAcceptes', poidsAcceptesCalcules);
      } else if (formData.poidsAcceptes !== poidsAcceptesCalcules && poidsAcceptesCalcules > 0) {
        // Si la valeur calculée change et est > 0, mettre à jour
        onInputChange('poidsAcceptes', poidsAcceptesCalcules);
      }
    } else {
      // Pour réceptions : calcul normal
      const poidsAcceptesCalcules = ((formData.nbSacsSains || 0) + ((formData.nbSacsRecond || 0) * FACTEUR_RECONDITIONNEMENT)) * conditionnementCapacite;
      if (formData.poidsAcceptes !== poidsAcceptesCalcules) {
        onInputChange('poidsAcceptes', poidsAcceptesCalcules);
      }
    }
  }, [formData.nbSacsSains, formData.nbSacsRecond, formData.nbSacsDeclares, formData.nbSacsAcceptes, formData.poidsDeclares, formData.poidsAcceptes, conditionnementCapacite, isExpeditionOrTransfert, onInputChange]);
  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Détails des Sacs
        </h3>
        <p className="text-sm text-gray-600">
          Saisissez les informations détaillées sur les sacs.
        </p>
      </div>
      
      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Nb de sacs déclarés</label>
          <input
            type="number"
            value={formData.nbSacsDeclares || 0}
            onChange={(e) => {
              onInputChange('nbSacsDeclares', parseInt(e.target.value) || 0);
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
            placeholder="0"
            min="0"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Nb de sacs sains</label>
          <input
            type="number"
            value={formData.nbSacsSains || 0}
            onChange={(e) => onInputChange('nbSacsSains', parseInt(e.target.value) || 0)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
            placeholder="0"
            min="0"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Nb de sacs Recond</label>
          <input
            type="number"
            value={formData.nbSacsRecond || 0}
            onChange={(e) => onInputChange('nbSacsRecond', parseInt(e.target.value) || 0)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
            placeholder="0"
            min="0"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Nb de sacs Humides</label>
          <input
            type="number"
            value={formData.nbSacsHumides || 0}
            onChange={(e) => onInputChange('nbSacsHumides', parseInt(e.target.value) || 0)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
            placeholder="0"
            min="0"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Nb de sacs déchirés</label>
          <input
            type="number"
            value={formData.nbSacsDechires || 0}
            onChange={(e) => onInputChange('nbSacsDechires', parseInt(e.target.value) || 0)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
            placeholder="0"
            min="0"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Nb de sacs rejetés</label>
          <input
            type="number"
            value={formData.nbSacsRejetes || 0}
            onChange={(e) => onInputChange('nbSacsRejetes', parseInt(e.target.value) || 0)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
            placeholder="0"
            min="0"
          />
        </div>
                 <div>
           <label className="block text-sm font-medium text-gray-700 mb-1">
             Nb de sacs acceptés 
             <span className="text-xs text-gray-500 ml-1">(calculé automatiquement)</span>
           </label>
           <input
             type="number"
             value={formData.nbSacsAcceptes || 0}
             readOnly
             className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm"
             placeholder="0"
           />
           <p className="text-xs text-gray-500 mt-1">
             {isExpeditionOrTransfert ? (
               <>={formData.nbSacsSains > 0 ? formData.nbSacsSains : formData.nbSacsDeclares || 0} {formData.nbSacsSains > 0 ? 'sains' : 'déclarés'}</>
             ) : (
               <>= {formData.nbSacsSains || 0} sains + {formData.nbSacsRecond || 0} recond</>
             )}
           </p>
         </div>
         <div>
           <label className="block text-sm font-medium text-gray-700 mb-1">
             Poids accepté (kg)
             <span className="text-xs text-gray-500 ml-1">(calculé automatiquement)</span>
           </label>
           <input
             type="number"
             value={formData.poidsAcceptes || 0}
             readOnly
             className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm"
             placeholder="0"
           />
           <p className="text-xs text-gray-500 mt-1">
             {isExpeditionOrTransfert ? (
               <>={formData.nbSacsSains > 0 ? formData.nbSacsSains : (formData.poidsDeclares || formData.nbSacsDeclares || 0)} kg (sacs de 1kg)</>
             ) : (
               <>= ({formData.nbSacsSains || 0} sains + ({formData.nbSacsRecond || 0} recond × {FACTEUR_RECONDITIONNEMENT})) sacs × {conditionnementCapacite} kg/sac</>
             )}
           </p>
         </div>
         </div>
      </div>
    </div>
  );
};

export default QualityAnalysisSection;
